import { isArray } from "lodash-es";
import { convertJsonObjectToLabeling, Labeling } from '../store/labeling';
import { convertJsonObjectToLicenseStatuses, LicenseStatus } from '../store/license-status';
import { convertJsonObjectToModelCustomizations, convertModelCustomizationsToJson, convertSingleModelJsonToModelCustomizationOutput } from '../store/model-customization-helpers';
import { ModelCustomizationEntities, ModelCustomizationEntitiesForExport, ModelCustomizationOutputEntities } from '../store/model-customization-types';
import { convertJsonObjectToMVisionClientList, MVisionAppClient } from '../store/mvision-client-list';
import { getAppConfig } from '../store/selectors';
import store from '../store/store';
import { JSONstringifyOrder } from '../util/string-convert';
import BackendApiInterface, { JsonValue } from './backend-api-interface';
import { convertDaemonConfigToJson, convertJsonObjectToDaemonConfig, convertJsonObjectToDaemonConfigs, DaemonConfig } from '../store/daemon-config';
import UserSettings, { UserSettingField } from "../store/user-settings";
import { AccessRights, parseAccessRights } from "../store/access-rights";



export type ModelsList = {
    availableModels: string[];
    deprecatedModels: string[];
}

/**
 * API wrapper for accessing backend's configuration API funtions. This class contains generic wrapper code that will,
 * in turn, call current correct implementation of the backend API client (cloud, local, dev-time, etc).
 */
export default class ConfigurationClient {

    /** Gets a backend client for any backend API calls matching whatever is the current
     * cloud backend.
     */
    private get api(): BackendApiInterface {
        const appConfig = getAppConfig(store.getState());
        if (appConfig === undefined) {
            throw new Error(`Could not get current app config.`);
        }

        return appConfig.getBackendApi();
    }

    /** Fetch ALL model customizations. */
    public async fetchModelCustomizationsAsync(appClient: MVisionAppClient | undefined): Promise<ModelCustomizationEntities> {
        const jsonContents = await this.api.fetchModelConfigurationFileAsync(appClient);
        const customizations = convertJsonObjectToModelCustomizations(jsonContents);
        return customizations;
    }

    /** Save the entire model customization config to backend in one go */
    public async saveModelCustomizationAsync(appClient: MVisionAppClient | undefined, modelCustomization: ModelCustomizationEntitiesForExport): Promise<boolean> {
        const jsonObject = convertModelCustomizationsToJson(modelCustomization);
        const json = JSONstringifyOrder(jsonObject);
        const success = await this.api.saveModelConfigurationAsync(appClient, json);
        return success;
    }

    /** Deletes ALL model customizations for a specific app client which should revert them to default factory settings. */
    public async deleteModelCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        const success = await this.api.deleteModelConfigurationAsync(appClient);
        return success;
    }

    public async fetchDefaultConfigurationForCustomizationOutput(modelName: string): Promise<ModelCustomizationOutputEntities> {
        const jsonContents = await this.api.fetchDefaultConfigurationForModelAsync(modelName);
        const customization = convertSingleModelJsonToModelCustomizationOutput(jsonContents);
        return customization;
    }

    /** Returns an array of all the segmentation model names that are available for current logged-in user (as per their auth),
      * or undefined if the list could not be retrieved correctly.
      */
    public async fetchAvailableModelsAsync(appClient: MVisionAppClient | undefined): Promise<ModelsList | undefined> {
        const activeModelsList = await this.api.fetchActiveModelsAsync(appClient);
        const deprecatedModels = [];

        if ('user_model_details' in activeModelsList && isArray(activeModelsList['user_model_details'])) {
            const availableModels: string[] = [];
            for (const model of activeModelsList['user_model_details']) {
                if ('model_name' in model) {
                    if (model['deprecated']) {
                        deprecatedModels.push(model['model_name']);
                    }
                    availableModels.push(model['model_name']);
                }
            }
            return { availableModels, deprecatedModels };
        }

        return undefined;
    }

    public async fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<LicenseStatus[]> {
        const jsonContents: any = await this.api.fetchLicenseStatusAsync(appClient);
        const licenseStatuses = convertJsonObjectToLicenseStatuses(jsonContents);
        return licenseStatuses;
    }

    public async saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<any> {
        const success = await this.api.saveLicenseAsync(appClient, newLicense);

        return success;
    }

    /** Fetches all daemon configurations for a specific app client. */
    public async fetchDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<DaemonConfig[]> {
        const jsonContents: any = await this.api.fetchAllDaemonConfigsAsync(appClient);
        const daemonConfigs = convertJsonObjectToDaemonConfigs(jsonContents);
        return daemonConfigs;
    }

    /** Fetches a single daemon config or undefined. */
    public async fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonClientId: string): Promise<DaemonConfig> {
        const json: any = await this.api.fetchDaemonConfigAsync(appClient, daemonClientId);
        const daemonConfig = convertJsonObjectToDaemonConfig(json);
        return daemonConfig;
    }

    /** Saves a single daemon configuration. */
    public async saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig): Promise<boolean> {
        const jsonObject = convertDaemonConfigToJson(daemonConfig);
        const json = JSONstringifyOrder(jsonObject);
        const success = await this.api.saveDaemonConfigAsync(appClient, json);
        return success;
    }

    /** Resets a specific daemon configuration of a specific app client to default factory settings. */
    public async resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig): Promise<boolean> {
        const success = await this.api.resetDaemonConfigAsync(appClient, daemonConfig.sessionId);
        return success;
    }

    /** Deletes a specific daemon configuration of a specific app client. */
    public async deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig): Promise<boolean> {
        const success = await this.api.deleteDaemonConfigAsync(appClient, daemonConfig.sessionId);
        return success;
    }

    public async fetchLabelingAsync(): Promise<Labeling> {
        const json = await this.api.fetchLabelingAsync();

        const labeling = convertJsonObjectToLabeling(json.labeling, json.backendVersion);
        return labeling;
    }

    public async fetchClientListAsync(): Promise<MVisionAppClient[]> {
        const clientListJsonContents = await this.api.fetchClientListAsync();

        const clientList = convertJsonObjectToMVisionClientList(clientListJsonContents);
        return clientList;
    }

    public async fetchAndParseAccessRights(): Promise<AccessRights> {
        const accessRightsJSON = await this.api.fetchAccessRightsAsync();
        const parsedAccessRights = parseAccessRights(accessRightsJSON);
        return parsedAccessRights;
    };


    public async fetchUserSettingsAsync(): Promise<UserSettings> {
        // see user-settings.tsx
        throw new Error('User settings are not implemented');

        // const userSettingsJson = await this.api.getUserSettingsAsync();

        // const userSettings = convertJsonObjectToUserSettings(userSettingsJson);
        // return userSettings;
    }

    public async saveUserSettingAsync(setting: UserSettingField, value: string | string[] | number | undefined): Promise<boolean> {
        // see user-settings.tsx
        throw new Error('User settings are not implemented');

        // return await this.api.saveUserSettingAsync(getUserSettingJsonFieldName(setting), JSON.stringify(value));
    }

    public async saveUserSettingsAsync(settings: { setting: UserSettingField, value: JsonValue }[]): Promise<boolean> {
        // see user-settings.tsx
        throw new Error('User settings are not implemented');
        
        // const settingsJson: {[x: string]: JsonValue} = {};
        // for (const setting of settings) {
        //     settingsJson[getUserSettingJsonFieldName(setting.setting)] = setting.value;
        // }
        // return await this.api.saveUserSettingsAsync(settingsJson);
    }
}
